'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { authService } from '@/services/auth-service';
import { Loading } from '@/components/ui/loading';

/**
 * 处理第三方接入的 token 交换逻辑
 * 在 layout 层前置执行,避免在页面层才开始认证
 * 只在页面首次加载时执行一次,不响应后续路由变化
 */
export function ThirdPartyAuthHandler({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  // 初始为 true,防止在 useEffect 执行前渲染 children
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    // 只在组件挂载时执行一次
    const pathname = window.location.pathname;

    // 不是 third-party 路径,允许渲染 children
    if (!pathname.startsWith('/third-party')) {
      setIsProcessing(false);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    const appId = searchParams.get('app_id');

    // 参数校验失败,直接跳转错误页
    if (!code || !appId) {
      router.replace(
        `/third-party/error?msg=${encodeURIComponent('参数错误')}`,
      );
      return;
    }

    // 执行 token 交换（isProcessing 初始就是 true）
    (async () => {
      try {
        await authService.thirdPartyLogin({ code, app_id: appId });
        // 认证成功,重置状态后跳转
        setIsProcessing(false);
        router.replace('/dashboard?from=third-party');
      } catch (error) {
        console.error('Third-party login failed:', error);
        // 认证失败,重置状态后跳转错误页
        setIsProcessing(false);
        router.replace(
          `/third-party/error?msg=${encodeURIComponent('登录失败')}`,
        );
      }
    })();
  }, []); // 空依赖数组,只在挂载时执行一次

  // 正在处理认证时,显示 loading,不渲染子组件
  if (isProcessing) {
    return (
      <div className="w-[100vw] h-[100vh] flex items-center justify-center">
        <Loading iconCls="w-8 h-8" />
      </div>
    );
  }

  // 认证已完成或非 third-party 路径,渲染子组件
  return <>{children}</>;
}
